import { FormControl, FormLabel, Text } from '@chakra-ui/react';
import mixpanel from 'mixpanel-browser';
import {
  isValidElement,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { FeatureTrackingEvents } from '@revelio/core';
import { ShareCaptureScope, useChartTheme } from '@revelio/replots';
import { Button, Checkbox, Dialog, Input } from '@revelio/ui';

import { useBrand } from '../../../../../shared/branding/brand-provider';
import styles from '../../Modal/themedModal.module.css';
import { usePageConfig } from '../../PageConfigProvider/PageConfigProvider';
import type { PositionedItem } from '../../PageConfigProvider/types/item';
import { useSharedReportData } from '../../SharedReport/SharedReportDataContext';
import { ChartItemOverride, useSafeChart } from '../ChartProvider';
import { isGroupedBarLegendHiddenOnPage } from '../utils/grouped-bar-legend-pages';
import { ChartMenuDataProvider } from './ChartMenuContext';
import localStyles from './DownloadPlotPng.module.css';
import { inlineComputedStyles } from './data-download/ppt-export/ppt-chart-image';
import { chartTitleWithSelection } from './data-download/ppt-export/ppt-slide-generator';
import layoutStyles from './download-plot-png.module.css';

const HEADER_HEIGHT = 44;
const FOOTER_HEIGHT = 13;
const CAPTURE_SCALE = 2;

/**
 * Chart types whose on-screen legend is switched by the "Show Legend" setting.
 * The PPT export always draws a legend for these, so when the setting is off
 * the PNG is taken from an off-screen copy that has it on. Add a chart type
 * here when it gains a "Show Legend" option.
 */
const LEGEND_SETTING_CHART_TYPES: ReadonlySet<string> = new Set([
  'line',
  'grouped-bar',
]);

/** How long the off-screen copy must stay unchanged before it counts as drawn. */
const SETTLE_MS = 150;
/** Stop waiting for the copy after this long and capture the on-screen chart. */
const GIVE_UP_MS = 2000;

/**
 * Grouped bars drop their legend when the card is too short for it (legend
 * 42px + 8px margin in replots), so their copy gets this much extra height
 * and the PNG grows by the same amount.
 */
const GROUPED_BAR_LEGEND_ROOM = 50;

/**
 * During a shared-report session the copy must not write a snapshot over the
 * real chart's, so its capture scope discards everything.
 */
const ignoreCapture = () => {};

/** True when the PNG needs an off-screen copy to show a legend the card hides. */
const needsLegendCopy = (
  item: PositionedItem | undefined,
  chartElement: ReactNode,
  pageId: string | undefined
): boolean =>
  item?.plot !== undefined &&
  item.plot.showLegend !== true &&
  LEGEND_SETTING_CHART_TYPES.has(item.plot.chartType ?? '') &&
  !(
    item.plot.chartType === 'grouped-bar' &&
    isGroupedBarLegendHiddenOnPage(pageId)
  ) &&
  isValidElement(chartElement);

/**
 * Resolves with the copy's chart element once the copy has stopped changing
 * for SETTLE_MS, or with null after GIVE_UP_MS. A chart draws in two passes
 * (measure its box, then redraw at that size with the legend), so capturing
 * on the first frame would miss the legend. Never rejects.
 */
const waitUntilSettled = (
  container: HTMLElement | null
): Promise<Element | null> =>
  new Promise((resolve) => {
    if (!container) {
      resolve(null);
      return;
    }
    const root = container;
    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    const observer = new MutationObserver(() => restartSettleTimer());
    const giveUpTimer = setTimeout(() => finish(null), GIVE_UP_MS);

    const finish = (result: Element | null) => {
      observer.disconnect();
      clearTimeout(settleTimer);
      clearTimeout(giveUpTimer);
      resolve(result);
    };
    // Every change inside the copy restarts the countdown; it completes only
    // once the copy has been quiet for SETTLE_MS.
    const restartSettleTimer = () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => finish(root.firstElementChild), SETTLE_MS);
    };

    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });
    restartSettleTimer();
  });

type DownloadPlotPNGProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  /** What the chart's dropdown is set to; the image title carries it. */
  selectionLabel?: string;
  printRef: RefObject<HTMLDivElement>;
  defaultFileName?: string;
  pageName?: string;
  chartId?: string;
  /** The chart element (as shown by Expand); drawn off-screen with its legend for the PNG. */
  chartElement?: ReactNode;
};

export const DownloadPlotPNG = ({
  isOpen,
  onClose,
  title,
  selectionLabel,
  printRef,
  defaultFileName,
  pageName,
  chartId,
  chartElement,
}: DownloadPlotPNGProps) => {
  const brand = useBrand();
  const { theme } = useChartTheme();
  const [transparentBackground, setTransparentBackground] = useState(false);
  const [fileName, setFileName] = useState('');
  const [plotTitle, setPlotTitle] = useState('');
  const fileNameId = useId();
  const plotTitleId = useId();
  const { item } = useSafeChart();
  const { config: pageConfig } = usePageConfig();
  const sharedReportData = useSharedReportData();

  // Size of the off-screen legend copy while a download is in progress; null
  // means no copy is mounted.
  const [legendCopySize, setLegendCopySize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const legendCopyRef = useRef<HTMLDivElement>(null);

  const imageTitle = chartTitleWithSelection(title, selectionLabel);

  useEffect(() => {
    if (isOpen) {
      setFileName(defaultFileName || title);
      setPlotTitle(imageTitle);
      setTransparentBackground(false);
    }
  }, [isOpen, title, imageTitle, defaultFileName]);

  const resetForm = useCallback(() => {
    setFileName(defaultFileName || title);
    setPlotTitle(imageTitle);
    setTransparentBackground(false);
  }, [title, imageTitle, defaultFileName]);

  const handleDownload = () => {
    const fileNameSnapshot = fileName;
    const plotTitleSnapshot = plotTitle;
    const transparentBackgroundSnapshot = transparentBackground;
    const isDarkSnapshot = theme === 'dark';

    mixpanel.track(FeatureTrackingEvents.PNG_DOWNLOAD, {
      plot_title: title,
      scope: 'plot',
      page: pageName,
      chart_id: chartId,
      transparent_background: transparentBackground,
    });

    // Line and grouped-bar legends are off on screen unless "Show Legend" is
    // ticked. Mount an off-screen copy with it ticked, at the card's size plus
    // any legend room; it renders during the delay below while the modal closes.
    const liveChart = printRef.current?.firstElementChild ?? null;
    const copyBox =
      liveChart && needsLegendCopy(item, chartElement, pageConfig.revelioPageId)
        ? liveChart.getBoundingClientRect()
        : null;
    if (copyBox) {
      const legendRoom =
        item?.plot?.chartType === 'grouped-bar' ? GROUPED_BAR_LEGEND_ROOM : 0;
      setLegendCopySize({
        width: copyBox.width,
        height: copyBox.height + legendRoom,
      });
    }

    onClose();

    // Defer heavy processing to allow modal to close cleanly
    setTimeout(() => {
      (async () => {
        // Capture the legend copy once it has finished drawing; if there is no
        // copy, or it never settles, fall back to the on-screen chart.
        const legendCopyChart = copyBox
          ? await waitUntilSettled(legendCopyRef.current)
          : null;
        const cardBody = printRef.current;
        const chartContainer =
          (legendCopyChart?.isConnected ? legendCopyChart : null) ??
          cardBody?.firstElementChild;
        if (!chartContainer) {
          setLegendCopySize(null);
          return;
        }

        const { width, height } = chartContainer.getBoundingClientRect();
        // Mirror the app surface and text rather than forcing white/brand: a dark-mode
        // capture on a white fill washes out the near-white chart text, and the brand
        // accent is already carried by the watermark.
        const bodyStyles = getComputedStyle(document.body);
        const themeBg =
          bodyStyles.getPropertyValue('--ui-chakra-bg-primary').trim() ||
          (isDarkSnapshot ? '#25282d' : '#ffffff');
        const themeText =
          bodyStyles.getPropertyValue('--ui-chakra-text-primary').trim() ||
          (isDarkSnapshot ? '#ffffff' : '#25282d');

        const chartNode = chartContainer.cloneNode(true);
        // The clone is all we need from the copy. Unmount it before anything
        // below can throw, so a failed export never leaves it mounted.
        setLegendCopySize(null);

        if (chartNode instanceof Element) {
          // html2canvas draws each inline svg as a standalone image, which
          // cannot see the document's stylesheets or `--ui-*` tokens. Bar and
          // axis labels take their font and fill from CSS-module classes, so
          // without this they fall back to the browser serif in black. Inline
          // the computed values from the live svg, as the PPT capture does.
          const liveSvgs = chartContainer.querySelectorAll('svg');
          const clonedSvgs = chartNode.querySelectorAll('svg');
          liveSvgs.forEach((liveSvg, i) => {
            const clonedSvg = clonedSvgs[i];
            if (clonedSvg) inlineComputedStyles(liveSvg, clonedSvg);
          });

          // Controls drawn inside the chart (the segment dropdown) are not part
          // of the image; the title carries their selection instead.
          chartNode
            .querySelectorAll<HTMLElement>('[data-export-hidden]')
            .forEach((control) => {
              control.style.visibility = 'hidden';
            });
        }

        const html2canvas = (await import('html2canvas-pro')).default;

        const imageEl = document.createElement('div');
        imageEl.style.width = `${width + 20}px`;
        const imageHeight = height + HEADER_HEIGHT + FOOTER_HEIGHT + 4;
        imageEl.style.height = `${imageHeight}px`;
        imageEl.style.padding = '0 10px';

        const headerEl = document.createElement('h1');
        headerEl.style.fontSize = width < 250 ? '1rem' : '1.5rem';
        headerEl.style.color = themeText;
        headerEl.style.fontWeight = '600';
        headerEl.style.marginBottom = '0.5rem';
        headerEl.style.textAlign = 'center';
        headerEl.style.width = '100%';
        headerEl.style.textOverflow = 'ellipsis';
        headerEl.style.whiteSpace = 'nowrap';
        headerEl.style.overflow = 'hidden';
        headerEl.textContent = plotTitleSnapshot;
        imageEl.appendChild(headerEl);

        const chartEl = document.createElement('div');
        chartEl.style.width = '100%';
        chartEl.style.height = `${height}px`;
        chartEl.style.margin = 'auto';
        chartEl.appendChild(chartNode);
        imageEl.appendChild(chartEl);

        const watermarkEl = document.createElement('img');
        watermarkEl.style.width = '85px';
        watermarkEl.style.height = `${FOOTER_HEIGHT}px`;
        watermarkEl.style.float = 'right';
        imageEl.appendChild(watermarkEl);

        // Convert the watermark SVG to a data URL so html2canvas can render it
        try {
          const resp = await fetch(brand.logos.watermark);
          const svgText = await resp.text();
          watermarkEl.src = `data:image/svg+xml;base64,${btoa(svgText)}`;
        } catch {
          watermarkEl.src = brand.logos.watermark;
        }

        document.body.append(imageEl);

        // A title wider than the chart wraps instead of being cut off, and the
        // image grows by the extra lines. Measured once in the document.
        const oneLineHeaderHeight = headerEl.offsetHeight;
        headerEl.style.whiteSpace = 'normal';
        headerEl.style.overflowWrap = 'anywhere';
        const extraHeaderHeight = headerEl.offsetHeight - oneLineHeaderHeight;
        if (extraHeaderHeight > 0) {
          imageEl.style.height = `${imageHeight + extraHeaderHeight}px`;
        }

        // Wait for the watermark image to load before capturing
        await new Promise<void>((resolve) => {
          if (watermarkEl.complete) {
            resolve();
          } else {
            watermarkEl.onload = () => resolve();
            watermarkEl.onerror = () => resolve();
          }
        });

        const canvas = await html2canvas(imageEl, {
          // Without this, html2canvas falls back to window.devicePixelRatio — 1 on a
          // non-Retina display, giving a soft export. Matches the ppt/economic-impact
          // captures.
          scale: Math.max(CAPTURE_SCALE, window.devicePixelRatio || 1),
          backgroundColor: transparentBackgroundSnapshot ? null : themeBg,
          onclone: (document_: Document) => {
            const marker = document.body.getAttribute('data-theme') ?? 'light';
            document_.documentElement.setAttribute('data-theme', marker);
            document_.body.setAttribute('data-theme', marker);
          },
        });

        const data = canvas.toDataURL();
        const link = document.createElement('a');

        if (typeof link.download === 'string') {
          link.href = data;
          link.download = `${fileNameSnapshot || 'plot'}.png`;
          document.body.appendChild(link);
          link.click();
          link.remove();
        } else {
          window.open(data);
        }

        imageEl.remove();
      })();
    }, 100);
  };

  // The chart's settings with "Show Legend" ticked, for the off-screen copy
  // only. A new object, so the real chart's settings are never modified.
  const itemWithLegend = item?.plot
    ? { ...item, plot: { ...item.plot, showLegend: true } }
    : undefined;

  const legendCopyContent = itemWithLegend ? (
    <ChartItemOverride item={itemWithLegend}>{chartElement}</ChartItemOverride>
  ) : null;

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        title={`${title} Plot Download`}
        placement="top"
        className={styles.content}
        footer={
          <div className={layoutStyles.footerActions}>
            <Button
              size="sm"
              variant="ghost"
              color="destructive"
              onClick={resetForm}
            >
              Reset
            </Button>
            <div>
              <Button
                size="sm"
                variant="link"
                className={localStyles.cancelBtn}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button color="primary" size="sm" onClick={handleDownload}>
                Download
              </Button>
            </div>
          </div>
        }
      >
        <FormControl isRequired className={localStyles.formField}>
          <FormLabel fontSize="sm" htmlFor={fileNameId}>
            File Name
          </FormLabel>
          <Input
            id={fileNameId}
            type="text"
            size="sm"
            required
            wrapperClassName={localStyles.fieldInput}
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
          />
        </FormControl>
        <FormControl className={localStyles.formField}>
          <FormLabel fontSize="sm" htmlFor={plotTitleId}>
            Title
          </FormLabel>
          <Input
            id={plotTitleId}
            type="text"
            size="sm"
            wrapperClassName={localStyles.fieldInput}
            value={plotTitle}
            onChange={(e) => setPlotTitle(e.target.value)}
          />
        </FormControl>
        <FormControl className={localStyles.formField}>
          <Checkbox
            checked={transparentBackground}
            onChange={() => setTransparentBackground((prevState) => !prevState)}
          >
            <Text fontSize="sm">Transparent Background</Text>
          </Checkbox>
        </FormControl>
      </Dialog>
      {/* Off-screen copy of the chart with its legend on, mounted only while a
          PNG download is in progress. It sits off the left edge instead of
          using display:none because charts size themselves from their box,
          and a hidden box measures 0×0. Its own chart-menu store keeps its
          writes away from the real chart's share/PPT state; the discarding
          capture scope is only needed while a shared-report session is on. */}
      {legendCopySize &&
        itemWithLegend &&
        createPortal(
          <div
            ref={legendCopyRef}
            aria-hidden
            style={{
              position: 'fixed',
              top: 0,
              left: -10000,
              width: legendCopySize.width,
              height: legendCopySize.height,
              pointerEvents: 'none',
            }}
          >
            <ChartMenuDataProvider>
              {sharedReportData ? (
                <ShareCaptureScope
                  scopeKey={`png-legend-copy-${itemWithLegend.id}`}
                  register={ignoreCapture}
                >
                  {legendCopyContent}
                </ShareCaptureScope>
              ) : (
                legendCopyContent
              )}
            </ChartMenuDataProvider>
          </div>,
          document.body
        )}
    </>
  );
};
